import { useAtom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault } from 'jotai/utils';
import { IFundMetricsResponseDataModel } from '../../data-models/fund-metrics.data-model';
import { FundMetrics, FundMetricsByFund, fundMetricsFormSchema } from '../../schemas/FundMetrics.schema';
import { getFundMetrics, getPlainDateString } from '../../services/queries/MaggieMetricsQueries';
import { fieldDisplayValuesFromSchema } from '../../util/schema-utils';
import { forceFundMetricsUpdateJ } from '../PortfolioOverview/state/MetricsStateJ';
import { selectedDateFPState, SelectedFPKey, selectedFundIdStateFP } from './state/FPState';

type FundMetricsValue = IFundMetricsResponseDataModel | null | Promise<IFundMetricsResponseDataModel | null>;

/**
 * The (date, fundId) key always determines which instance you read.
 *
 * Reactivity to `forceFundMetricsUpdateJ` applies **only on the initial
 * fetch**, while the atom is still in `atomWithDefault`'s unset / default
 * mode — incrementing the counter at that point re-runs the factory and
 * refetches. Once {@link useFundMetricsFP} calls `setSelectedFundMetrics`
 * (which `useInvalidateFundMetrics` does on every Waterfall invalidation),
 * the atom flips to override mode and the factory never runs again for
 * that key, so subsequent counter increments are inert for this atom.
 *
 * The Waterfall path is fine because `useInvalidateFundMetrics` performs
 * its own explicit refetch + `setSelectedFundMetrics`. Cross-domain
 * invalidations from Finance2 (`useInvalidateFinanceData::invalidateMetrics`)
 * refresh `fundMetricsByDateStateJ` for the portfolio-overview page but
 * will not reach this atom once it has been written.
 */
const selectedFundMetricsStateFPJ = atomFamily(
  ({ date, fundId }: SelectedFPKey) =>
    atomWithDefault<FundMetricsValue>(async (get) => {
      if (!fundId) {
        return null;
      }
      get(forceFundMetricsUpdateJ);
      return getFundMetrics(getPlainDateString(date), [fundId]);
    }),
  (a, b) => a.date.getTime() === b.date.getTime() && a.fundId === b.fundId
);

export function useFundMetricsFP() {
  const date = useAtomValue(selectedDateFPState);
  const fundId = useAtomValue(selectedFundIdStateFP);

  const [fundMetrics, setSelectedFundMetrics] = useAtom(selectedFundMetricsStateFPJ({ date, fundId }));
  const metrics = fundMetrics?.metrics?.at(0);

  return {
    metrics: metrics as FundMetricsByFund | undefined,
    setSelectedFundMetrics,
  };
}

export function getFundMetricsAdditionalData(metrics: FundMetrics) {
  const fields = fundMetricsFormSchema();
  return fieldDisplayValuesFromSchema({
    schema: fields,
    values: metrics,
    paths: ['lpContributions', 'gpContributions', 'lpDistributions', 'gpDistributions'],
  });
}
